import React, {useContext, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState} from "react";
import UserHomepageColorTheme from "../color-theme";
import {UserHomepageControllerContext, UserHomepageViewContext} from "../../../index";
import classnames from "classnames";
import {UserHomepageControllerEvents, UserHomePageTypes} from "../../../models";
import useWindowViewportWidth from "../../../../../hooks/use-window/viewport-width";
import UserPhoneHomepage from "../../phone";
import UserWebHomepage from "../../web";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import ObserversService from "../../../../../../core/services/observers";
import Utils from "../../../../../../core/services/utils/utils";

/**
 * De-bounces the given callback for the given timeout.
 * @param callback
 * @param timeout
 * @return {(function(...[*]): void)}
 */
const debounce = (callback, timeout = 200) => {
    let timer = undefined;
    return (...args) => {
        if (timer) {
            clearTimeout(timer);
            timer = undefined;
        }
        timer = setTimeout(() => callback(...args), timeout)
    }
}

const UserHomepageView = ({
                              viewType,
                              boundedHeight = true,
                              boundedWidth = true,
                              viewportWidth = 'auto',
                              bodyId,
                              deviceId,
                              containerId,
                              ...props
                          }, ref) => {
    const windowViewportWidth = useWindowViewportWidth();
    const controller = useContext(UserHomepageControllerContext);
    const [wrapperStyle, setWrapperStyle] = useState({});
    const [state, setState] = useState({zoom: 1, viewportWidth: viewportWidth});
    /** @type {React.MutableRefObject<HTMLDivElement>} */
    const deviceWrapperRef = useRef();
    const isMounted = useIsMounted();

    /**
     * With each change in the controller and wrapper style state:
     * - attaches event an listener that listens for the creation of a component, and then resets the styles of the
     * device.
     */
    useEffect(() => {
        controller.on(UserHomepageControllerEvents.onComponentCreated, resetStyles);
        return () => {
            controller.off(UserHomepageControllerEvents.onComponentCreated, resetStyles);
        }
    }, [controller, wrapperStyle])

    /**
     * Resets the styles of the device to prevent the scrolling bug of the device.
     * @return {Promise<void>}
     */
    const resetStyles = async () => {
        await Utils.wait(1500);
        const view = document.querySelector('.view-wrapper');
        view.style.overflowY = 'auto';
        view.scrollTo(0, 0);
        await Utils.wait(100);
        view.style.overflowY = 'hidden';
    }

    /**
     * Sets the wrapper styles with the given state.
     * @param state
     */
    const _adjustStyles = (state) => {
        const device = deviceWrapperRef.current?.querySelector('.marvel-device');
        if (!device)
            return;

        const deviceHeight = device?.getBoundingClientRect()?.height ?? 0;
        const wrapperHeight = deviceHeight + 40;
        const deviceHeightBeforeTransform = (deviceHeight / state.zoom) - (20 / state.zoom);
        const translateY = -((deviceHeightBeforeTransform - deviceHeight) / 2) / state.zoom;

        if (!isMounted())
            return;
        setWrapperStyle(prevState => ({
            ...prevState,
            height: wrapperHeight,
            translateY: translateY,
        }));
    }

    /**
     * Adjusts the devices' zoom value with each change in the size of the available width.
     * * this effect is for the purpose of always imitating a full desktop size for the web view.
     */
    const _adjustWebDeviceZoom = () => {
        const bodyRect = document.getElementById(bodyId)?.getBoundingClientRect();
        const bodyHeight = bodyRect?.height ?? 0;

        const containerRect = document.getElementById(containerId)?.getBoundingClientRect();
        const containerWidth = containerRect?.width ?? 0

        const wrapperRect = deviceWrapperRef.current?.querySelector('div')?.getBoundingClientRect();
        const wrapperWidth = wrapperRect?.width ?? 0

        const deviceRect = deviceWrapperRef.current?.querySelector('.marvel-device')?.getBoundingClientRect();
        const deviceWidth = deviceRect?.width ?? 0;
        const deviceHeight = deviceRect?.height ?? 0;

        if (!isMounted())
            return;
        setState(prevState => {
            const prevDeviceWidth = (deviceWidth / prevState.zoom);
            const prevDeviceHeight = (deviceHeight / prevState.zoom);
            let zoom = containerWidth / prevDeviceWidth;

            if (bodyHeight < (zoom * prevDeviceHeight) && boundedHeight) {
                zoom = Math.min((bodyHeight - 40) / prevDeviceHeight, zoom);
            }

            if (wrapperWidth < (zoom * prevDeviceWidth) && boundedWidth) {
                zoom = Math.min(wrapperWidth / prevDeviceWidth, zoom)
            }

            return {
                ...prevState,
                zoom: zoom,
            }
        })
    }

    const adjustStyles = useRef(debounce(_adjustStyles, 300));
    const adjustWebDeviceZoom = useRef(debounce(_adjustWebDeviceZoom, 100));

    /**
     * Exposes the state of this context to the parent component.
     */
    useImperativeHandle(ref, () => ({
        ...state,
    }), [state])

    /**
     * With each change in the viewport width:
     * - sets the context' viewport width.
     */
    useLayoutEffect(() => {
        setState(prevState => ({
            ...prevState,
            viewportWidth: viewportWidth ?? windowViewportWidth,
        }))
    }, [viewportWidth])

    /**
     * With each change in the state:
     * - adjusts the style of the wrapper with the new zoom.
     */
    useLayoutEffect(() => {
        adjustStyles.current(state);
    }, [state.zoom])


    /**
     * As soon as the component mounts:
     * - attaches an observer that would adjust the height of the device wrapper with each change in the device size.
     */
    useEffect(() => {
        const container = document.getElementById(containerId);
        if (!container)
            return;
        const throttledCallback = debounce((entries, observer) => changeViewState(entries, observer), 100);
        const observer = ObserversService.newResizeObserver(throttledCallback);
        ObserversService.observeResizeObserver(observer, container);
        throttledCallback()
        return () => ObserversService.disconnectResizeObserver(observer)
    }, [containerId, boundedHeight, boundedWidth, bodyId, deviceId])

    /**
     * Adjusts the devices' zoom value with each change in the size of the available width.
     * * this effect is for the purpose of always imitating a full desktop size for the web view.
     */
    const changeViewState = () => {
        requestAnimationFrame(() => {
            if (!isMounted())
                return;
            switch (viewType) {
                case UserHomePageTypes.phone:
                    return adjustPhoneDeviceZoom();
                case UserHomePageTypes.web:
                    return adjustWebDeviceZoom.current();
                default:
                    return;
            }
        })

    }

    /**
     * Adjusts the devices' zoom value with each change in the size of the available width.
     * * as of now, nothing happens here.
     */
    const adjustPhoneDeviceZoom = () => {
        // TODO: Adjust the size based on the given viewport size or determine it if 'auto'
        if (!isMounted())
            return;
        setState({
            zoom: 0.8,
            viewportWidth: viewportWidth ?? windowViewportWidth,
        })
    }


    const content = useMemo(() => (
        <UserHomepageColorTheme viewType={viewType}>
            {
                viewType === UserHomePageTypes.phone &&
                <UserPhoneHomepage id={deviceId}/>
            }
            {
                viewType === UserHomePageTypes.web &&
                <UserWebHomepage id={deviceId}/>
            }
        </UserHomepageColorTheme>
    ), [viewType, deviceId])

    return (
        <>
            <UserHomepageViewContext.Provider value={state}>
                <div
                    {...props}
                    className={classnames(
                        'view-wrapper',
                        props?.className,
                        {
                            [viewType]: true
                        }
                    )}
                    ref={deviceWrapperRef}
                    style={{
                        ...props?.style,
                        height: `${wrapperStyle.height ?? 0}px`,
                        '--view-zoom': state.zoom,
                        '--view-translate-y': `${wrapperStyle.translateY ?? 0}px`,
                    }}
                >
                    {content}
                </div>
            </UserHomepageViewContext.Provider>
        </>
    )
}

export default UserHomepageView;
